import React, { useEffect, useState } from 'react';
import { FormState } from 'react-hook-form';
import { registrySchema, registryType } from 'types/zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { AdmDocumentType, ResidenceMode, StepComponentProps } from 'types';
import FormStepManager from 'components/Application/Form/FormStepManager/FormStepManager';
import BlankLoader from 'components/Shared/Spinner/BlankLoader';

import DatePicker from 'react-datepicker';
import { CustomDatePickerInput } from 'components/reactDatepicker';
import dayjs from 'dayjs';
import { FaFingerprint } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IResidence } from 'types/api/residences';

// setDefaultLocale(it);

function DescriptionBar({
  formState,
  requiredFields,
  residence,
}: {
  formState: FormState<registryType>;
  requiredFields: number;
  residence: IResidence;
}) {
  const { t } = useTranslation();
  const { errors, isValid: isFormValid } = formState;
  const errorsNumber = Object.keys(errors).length;
  const progressPercentage = ((requiredFields - errorsNumber) / requiredFields) * 100;
  // console.log(progressPercentage)
  return (
    <div className="animate-fade-in-down md:animate-fade-in-left mt-4 md:mt-0 flex w-full md:w-4/12 flex-col items-center md:items-start gap-4">
      <h3 className="flex flex-row items-center text-black font-bold text-xl">
        <FaFingerprint />
        <span className="pl-2">{t('DescriptionBar.title')}</span>
      </h3>
      <p className="text-black font-medium text-md">{t('DescriptionBar.description')}</p>
      <ul className="list-disc text-black font-medium text-md pl-4">
        <li>{t('DescriptionBar.option_citizenship')}</li>
        {residence.admRequiredDocuments.some((doc) => doc.key === AdmDocumentType.ISEE) ? (
          <li>{t('DescriptionBar.option_isee')}</li>
        ) : null}
      </ul>
    </div>
  );
}
export default function Registry({
  steps,
  currentStep,
  nextStep,
  prevStep,
  profile,
  profileFetchStatus,
  applicationAppeal,
  applicationAppealResidence,
  setProfile,
}: StepComponentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, setValue, trigger, reset, getValues, watch, handleSubmit, control, formState } =
    useForm<registryType>({
      resolver: zodResolver(registrySchema),
      defaultValues: {
        firstName: undefined,
        lastName: undefined,
        fiscalCode: undefined,
        placeOfBirth: undefined,
        birthday: undefined,
        citizenship: undefined,
        secondCitizenship: undefined,
        notSubmitIsee: undefined,
        isee: undefined,
        address: {
          street: undefined,
          state: undefined,
          number: undefined,
          zipCode: undefined,
          district: undefined,
          city: undefined,
        },
        phoneNumber: undefined,
        cellNumber: undefined,
      },
      mode: 'all',
    });

  const { errors } = formState;

  const [isSteploading, setIsStepLoading] = useState<boolean>(true);

  const fieldRef = React.useRef<HTMLInputElement>(null);

  const watchNotSubmitIsee = watch('notSubmitIsee');

  useEffect(() => {
    if (watchNotSubmitIsee) {
      setValue('isee', null);
    }
  }, [watchNotSubmitIsee]);

  useEffect(() => {
    if (profile) {
      reset({
        ...profile.registry,
      });
    }
    // trigger()
    setIsStepLoading(false);
  }, [profile]);

  const submitForm = handleSubmit(async (data) => {
    // console.log(`${currentStep.name} form submitted: `, data)
    const parsedRegistry = registrySchema.parse(data);
    setProfile({ ...profile, registry: parsedRegistry });

    nextStep && navigate(`/application/form/${nextStep.path}`);
  });

  const [isStepComplete, setIsStepComplete] = useState<boolean>(false);

  useEffect(() => {
    if (nextStep && formState.isValid) setIsStepComplete(true);
    else setIsStepComplete(false);
  }, [formState.isValid, nextStep]);

  return (
    <div ref={fieldRef} className="w-full flex flex-col">
      <FormStepManager
        profile={profile}
        steps={steps}
        prevStep={prevStep}
        currentStep={currentStep}
        nextStep={isStepComplete ? submitForm : undefined}
      />
      {isSteploading ? (
        <BlankLoader />
      ) : (
        <div className="w-full flex flex-col md:flex-row justify-between flex-wrap">
          <DescriptionBar requiredFields={13} formState={formState} residence={applicationAppealResidence!} />
          <div className="animate-fade-in-up md:animate-fade-in-right w-full md:w-8/12 flex flex-col  items-start md:pl-8 mt-8 md:mt-0">
            <h3 className="form-title">{t('Registry.title')}</h3>
            <div className="flex flex-col md:flex-row w-full justify-between gap-4 ">
              <div className="w-full md:w-1/2 mb-4">
                <label className="form-label required" htmlFor="firstName">
                  {t('Registry.firstName')}
                </label>
                <input
                  {...register('firstName', { required: true })}
                  defaultValue={undefined}
                  className={`form-input ${errors.firstName && 'error'}`}
                  id="firstName"
                  type="text"
                  placeholder={t('Registry.firstName')}
                />
                <p className="input-error">{errors.firstName?.message ? t(errors.firstName?.message) : ''}</p>
              </div>
              <div className="w-full md:w-1/2 mb-4">
                <label className="form-label required" htmlFor="lastName">
                  {t('Registry.lastName')}
                </label>
                <input
                  {...register('lastName', { required: true })}
                  defaultValue={undefined}
                  className={`form-input ${errors.lastName && 'error'}`}
                  id="lastName"
                  type="text"
                  placeholder={t('Registry.lastName')}
                />
                <p className="input-error">{errors.lastName?.message ? t(errors.lastName?.message) : ''}</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row w-full justify-between gap-4">
              {applicationAppealResidence?.mode &&
              [ResidenceMode.INTERNATIONAL, ResidenceMode.GRADUATED].includes(applicationAppealResidence.mode) ? (
                <div className="w-full md:w-1/2 mb-4">
                  <label className="form-label required" htmlFor="placeOfBirth">
                    {t('Registry.placeOfBirth')}
                  </label>
                  <input
                    {...register('placeOfBirth', { required: true })}
                    defaultValue={undefined}
                    className={`form-input ${errors.placeOfBirth && 'error'}`}
                    id="placeOfBirth"
                    type="text"
                    placeholder={t('Registry.placeOfBirth')}
                  />
                  <p className="input-error">{errors.placeOfBirth?.message ? t(errors.placeOfBirth?.message) : ''}</p>
                </div>
              ) : null}
              <div className="w-full md:w-1/2 mb-4">
                <label className="form-label required" htmlFor="birthday">
                  {t('Registry.birthday')}
                </label>
                {/* onChange={(e) => setEmail(e.target.value)} */}
                <Controller
                  control={control}
                  name="birthday"
                  render={({ field }) => (
                    <>
                      {/* <div className='hidden xl:block'> */}
                      <DatePicker
                        placeholderText={t('Registry.birthday_paceholder')}
                        dateFormat={'dd/MM/yyyy'}
                        // onChange={(date: Date) => { console.log(dayjs(date).utc().startOf('day').toDate()); field.onChange(dayjs(date).utc().startOf('day').toDate()) }}
                        // onChange={(date: Date) => { console.log(dayjs(date).format(), dayjs.utc(date).format()); field.onChange(dayjs(date).utc().startOf('day').toDate()) }}
                        onChange={(date: Date) => {
                          field.onChange(dayjs(date).startOf('day').toDate());
                        }}
                        selected={field.value}
                        withPortal
                        customInput={React.createElement(React.forwardRef(CustomDatePickerInput))}
                        showMonthDropdown
                        showYearDropdown
                        minDate={dayjs().subtract(40, 'year').toDate()}
                        maxDate={dayjs().toDate()}
                        dropdownMode="select"
                        onKeyDown={(e) => {
                          e.preventDefault();
                        }}
                        locale={'it-IT'}
                      />
                      {/* </div> */}
                      {/* <input className={`block xl:hidden form-input ${errors.birthday && 'error'}`} value={field.value && dayjs.utc(field.value).format('YYYY-MM-DD')} onChange={(e) => { const value = e.target.value; console.log(dayjs(value, 'YYYY-MM-DD').format(),dayjs.utc(value, 'YYYY-MM-DD').format()); field.onChange(value && value !== "" ? dayjs(value, 'YYYY-MM-DD').startOf('day').utc(true).toDate() : undefined) }} id="birthday" type="date" placeholder="DD/MM/YYYY" onKeyDown={(e) => e.preventDefault()} /> */}
                      {/* <input className={`block xl:hidden form-input ${errors.birthday && 'error'}`} value={field.value && dayjs.utc(field.value).format('YYYY-MM-DD')} onChange={(e) => { const value = e.target.value; field.onChange(value && value !== "" ? dayjs.utc(value, 'YYYY-MM-DD').startOf('day').toDate() : undefined) }} id="birthday" type="date" placeholder="DD/MM/YYYY" onKeyDown={(e) => e.preventDefault()} /> */}
                    </>
                  )}
                />
                <p className="input-error">{errors.birthday?.message ? t(errors.birthday?.message) : ''}</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row w-full justify-between gap-4">
              <div className="w-full md:w-1/2 mb-4">
                <label className="form-label required" htmlFor="citizenship">
                  {t('Registry.citizenship')}
                </label>
                <input
                  {...register('citizenship', { required: true })}
                  className={`form-input ${errors.citizenship && 'error'}`}
                  id="citizenship"
                  type="text"
                  placeholder={t('Registry.citizenship')}
                />
                <p className="input-error">{errors.citizenship?.message ? t(errors.citizenship?.message) : ''}</p>
              </div>
              {applicationAppealResidence?.mode &&
              ![ResidenceMode.INTERNATIONAL, ResidenceMode.GRADUATED].includes(applicationAppealResidence.mode) ? (
                <div className="w-full md:w-1/2 mb-4">
                  <label className="form-label" htmlFor="secondCitizenship">
                    {t('Registry.secondCitizenship')}
                  </label>
                  <input
                    {...register('secondCitizenship', { required: true })}
                    className={`form-input ${errors.secondCitizenship && 'error'}`}
                    id="secondCitizenship"
                    type="text"
                    placeholder={t('Registry.secondCitizenship')}
                  />
                  <p className="input-error">
                    {errors.secondCitizenship?.message ? t(errors.secondCitizenship?.message) : ''}
                  </p>
                </div>
              ) : null}
            </div>
            {applicationAppealResidence?.mode &&
            applicationAppealResidence.admRequiredDocuments.some((doc) => doc.key === AdmDocumentType.ISEE) ? (
              <div className="flex flex-col md:flex-row w-full justify-between gap-4">
                <div className="w-full md:w-1/2 mb-4 flex flex-col">
                  <label className="form-label" htmlFor="notSubmitIsee">
                    {t('Registry.notSubmitIsee')}
                  </label>
                  <input
                    {...register('notSubmitIsee', {
                      setValueAs: (v) => (v ? !!v : null),
                    })}
                    className={`form-input w-4 h-4 mt-2 p-0 mb-4 ${errors.notSubmitIsee && 'error'}`}
                    id="notSubmitIsee"
                    type="checkbox"
                  />
                  <p className="text-xs text-black p-1">{t('Registry.notSubmitIsee_help')}</p>
                  <p className="input-error">{errors.isee?.message ? t(errors.isee?.message) : ''}</p>
                </div>

                {!watchNotSubmitIsee && (
                  <div className="w-full md:w-1/2 mb-4">
                    <label className="form-label" htmlFor="isee">
                      {t('Registry.isee')}
                    </label>
                    <input
                      {...register('isee', {
                        setValueAs: (v) => (v ? parseInt(v) : null),
                      })}
                      className={`form-input ${errors.isee && 'error'}`}
                      id="isee"
                      type="number"
                    />
                    <p className="text-xs text-black p-1">{t('Registry.isee_help')}</p>
                    <p className="input-error">{errors.isee?.message ? t(errors.isee?.message) : ''}</p>
                  </div>
                )}
              </div>
            ) : null}

            {![ResidenceMode.INTERNATIONAL, ResidenceMode.GRADUATED].includes(applicationAppealResidence!.mode) ? (
              <div className="w-full mb-4">
                <label className="form-label required" htmlFor="fiscalCode">
                  {t('Registry.fiscalCode')}
                </label>
                <input
                  {...register('fiscalCode', { required: true })}
                  className={`form-input ${errors.fiscalCode && 'error'}`}
                  id="fiscalCode"
                  type="text"
                  placeholder={t('Registry.fiscalCode')}
                />
                <p className="input-error">{errors.fiscalCode?.message ? t(errors.fiscalCode?.message) : ''}</p>
              </div>
            ) : null}

            <h3 className="text-bluemazza font-bold text-lg mb-4">{t('Registry.address_title')}</h3>
            <div className="flex flex-col md:flex-row w-full justify-between gap-4">
              <div className="w-full md:2/3 mb-4">
                <label className="form-label required" htmlFor="address">
                  {t('Registry.addressStreet')}
                </label>
                <input
                  {...register('address.street', { required: true })}
                  className={`form-input ${errors.address?.street && 'error'}`}
                  id="address.street"
                  type="text"
                  placeholder={t('Registry.addressStreet')}
                />
                <p className="input-error">
                  {errors.address?.street?.message ? t(errors.address?.street?.message) : ''}
                </p>
              </div>
              <div className="w-full md:w-1/3 mb-4">
                <label className="form-label required" htmlFor="addressNumber">
                  {t('Registry.addressNumber')}
                </label>
                <input
                  {...register('address.number', { required: true })}
                  className={`form-input ${errors.address?.number && 'error'}`}
                  id="address.number"
                  type="text"
                  placeholder={t('Registry.addressNumber')}
                />
                <p className="input-error">
                  {errors.address?.number?.message ? t(errors.address?.number?.message) : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row w-full justify-start gap-4">
              <div className="w-full md:w-1/4 mb-4">
                <label className="form-label required" htmlFor="zipCode">
                  {t('Registry.zipCode')}
                </label>
                <input
                  {...register('address.zipCode', { required: true, minLength: 2 })}
                  className={`form-input ${errors.address?.zipCode?.message && 'error'}`}
                  id="address.zipCode"
                  type="number"
                  placeholder={t('Registry.zipCode')}
                />
                <p className="input-error">
                  {errors.address?.zipCode?.message ? t(errors.address?.zipCode?.message) : ''}
                </p>
              </div>

              <div className="w-full md:w-1/4 mb-4">
                <label className="form-label required" htmlFor="city">
                  {t('Registry.city')}
                </label>
                <input
                  {...register('address.city', { required: true })}
                  className={`form-input ${errors.address?.city && 'error'}`}
                  id="address.city"
                  type="text"
                  placeholder={t('Registry.city')}
                />
                <p className="input-error">{errors.address?.city?.message ? t(errors.address?.city?.message) : ''}</p>
              </div>

              {![ResidenceMode.INTERNATIONAL, ResidenceMode.GRADUATED].includes(applicationAppealResidence!.mode) ? (
                <div className="w-full md:w-1/4 mb-4">
                  <label className="form-label required" htmlFor="address.district">
                    {t('Registry.district')}
                  </label>
                  <input
                    {...register('address.district', { required: true })}
                    className="form-input"
                    id="address.district"
                    type="text"
                    placeholder={t('Registry.district')}
                  />
                  <p className="input-error">
                    {errors.address?.district?.message ? t(errors.address?.district?.message) : ''}
                  </p>
                </div>
              ) : null}

              <div className="w-full md:w-1/4 mb-4">
                <label className="form-label required" htmlFor="state">
                  {t('Registry.state')}
                </label>
                <input
                  {...register('address.state', { required: true })}
                  className={`form-input ${errors.address?.state?.message && 'error'}`}
                  id="address.state"
                  type="text"
                  placeholder={t('Registry.state')}
                />
                <p className="input-error">{errors.address?.state?.message ? t(errors.address?.state?.message) : ''}</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row w-full justify-between gap-4 ">
              <div className="w-full md:w-1/2 mb-4">
                <label className="form-label required" htmlFor="cellNumber">
                  {t('Registry.cellNumber')}
                </label>
                <input
                  {...register('cellNumber', { required: true })}
                  className={`form-input ${formState.errors.cellNumber && 'error'}`}
                  id="cellNumber"
                  type="text"
                  placeholder={t('Registry.cellNumber')}
                />
                <p className="input-error">{errors.cellNumber?.message ? t(errors.cellNumber?.message) : ''}</p>
              </div>
              {![ResidenceMode.INTERNATIONAL, ResidenceMode.GRADUATED].includes(applicationAppealResidence!.mode) ? (
                <div className="w-full md:w-1/2 mb-4">
                  <label className="form-label" htmlFor="telNumber">
                    {t('Registry.telNumber')}
                  </label>
                  <input
                    {...register('phoneNumber', { required: true })}
                    className={`form-input ${formState.errors.phoneNumber && 'error'}`}
                    id="telNumber"
                    type="text"
                    placeholder={t('Registry.telNumber')}
                  />
                  <p className="input-error">{errors.phoneNumber?.message ? t(errors.phoneNumber?.message) : ''}</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
      <button
        className="bluemazza-submit-button md:self-end"
        disabled={!isStepComplete}
        onClick={isStepComplete ? submitForm : undefined}
      >
        {t('Registry.continue')}
      </button>
    </div>
  );
}
